import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './App.tsx';
import { assertSupabaseConfig } from './api/supabase.ts';
import { detectLocale, initI18n } from './i18n/index.ts';
import { localeFromPathname } from './i18n/routes.ts';
import { registerServiceWorker } from './push/registerServiceWorker.ts';
import './styles.css';

const root = document.getElementById('root');
if (!root) throw new Error('#root not found');

// Fail fast: a misconfigured deploy (missing VITE_SUPABASE_URL /
// VITE_SUPABASE_ANON_KEY) throws here, before anything renders, rather than
// later inside AuthProvider's effect.
assertSupabaseConfig();

// The URL decides the language (ADR-0015). Reading it here rather than letting
// the route loader do it means the correct catalogue is the one that loads,
// instead of loading the browser-detected one and immediately replacing it.
// Falls back to detection only for a path that carries no locale prefix — `/`,
// or a legacy link about to be redirected.
const initialLocale = localeFromPathname(window.location.pathname) ?? detectLocale();

// Await the catalogue before the first paint. The alternative — rendering with
// keys and swapping to text a tick later — flashes raw `login.submit` strings at
// the customer on a slow connection, which is the audience this PWA targets.
// One locale chunk is a few KB; the wait is not perceptible.
void initI18n(initialLocale).then(() => {
  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
});

// Register after `load`, not before: this competes for the same network and
// main-thread time as first paint, and the audience this PWA targets
// (mid-range Android phones on mobile data) feels that contention
// (`.claude/skills/pwa-shell/SKILL.md`). `registerServiceWorker` itself
// guards browsers with no `serviceWorker` support and swallows registration
// failures — either way the shell must still render.
window.addEventListener('load', registerServiceWorker);
